import _ from 'lodash';
import React, {FC, useEffect, useRef} from 'react';
import {connect} from 'react-redux';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {splitConversationsIntoRecentAndOlder} from '../../../helpers/models/conversationHelpers';
import {ResolveDispatchProps, useAppDispatch} from '../../../helpers/reduxHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {InboxesSearchLocationsEnum} from '../../../models/url/models/inboxesUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {ThunkAction} from '../../../store/appThunk';
import {getConversations} from '../../../store/data/dataSelectors';
import {markHighlightAsViewedAction} from '../../../store/metadata/recentCommunications/recentCommunicationsActions';
import {makeGetRecentCommunications} from '../../../store/metadata/recentCommunications/recentCommunicationsSelectors';
import {RecentCommunicationsBannerStatusesEnum} from '../../../store/metadata/recentCommunications/recentCommunicationsState';
import {
  contactDetailsScrolledToSectionAction,
  fetchContactDetailsConversationsAction,
} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {makeGetContactDetailsPanel} from '../../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsScrollSectionsEnum} from '../../../store/panels/contacts/contactDetailsPanelState';
import {getViewerPanel} from '../../../store/panels/panelsSelectors';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {ViewerPanelPageTypesEnum} from '../../../store/panels/viewer/viewerPanelState';
import {searchByParametersAction} from '../../../store/stateless/searchActions';
import {ConversationList} from '../../common/conversations/conversationList';
import {
  buildClickTrackingPayload,
  buildOpenConversationPayload,
  ContactDetailsClickActionsEnum,
  ContactDetailsClickLocationsEnum,
  ContactDetailsOpenConversationSectionsEnum,
} from '../contactDetailsTrackingHelpers';

/*
 * Props.
 */

interface ContactDetailsConversationListContainerProps {
  panelId: ContactDetailsPanelId;
  card: CardModel | undefined;
  visualContact: VisualContactModel;
  onConversationHoverTrack?: Handler;
}

interface MergeProps {
  onConversationClick: HandlerOf<ConversationModel>;
}

type ContactDetailsConversationListContainerComponentProps = ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps> &
  MergeProps &
  ContactDetailsConversationListContainerProps;

/*
 * Selectors.
 */

function makeGetConversationIds(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel], (panel) => panel?.conversationIds ?? []);
}

function makeGetHasMoreConversations(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel], (panel) => panel?.hasMoreConversations);
}

function makeGetConversations(getConversationIds: ReturnType<typeof makeGetConversationIds>) {
  return createSelector([getConversations, getConversationIds], (conversations, conversationIds) =>
    _(conversationIds)
      .uniq()
      .map((conversationId) => conversations[conversationId])
      .compact()
      .value(),
  );
}

function getContactUid(_state: AppState, props: ContactDetailsConversationListContainerProps) {
  return props.visualContact.contact.uid;
}

function makeGetConversationIdsToHighlight() {
  return createSelector(
    [makeGetRecentCommunications(getContactUid), getViewerPanel],
    (recentCommunications, viewerPanel) => {
      if (!recentCommunications || !viewerPanel) {
        return [];
      }

      // We only want to highlight recent conversations if the user views the P4 Contacts plugin by
      // clicking on the recent communications banner in P3
      if (recentCommunications.bannerStatus !== RecentCommunicationsBannerStatusesEnum.BANNER_CLICKED) {
        return [];
      }

      // We don't want to highlight the current conversation that's in the viewer panel
      const currentPage = viewerPanel.pages.current;
      const currentConversationId =
        currentPage?.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION
          ? currentPage.conversationId
          : undefined;
      return (recentCommunications.conversationIds ?? []).filter((id) => id !== currentConversationId);
    },
  );
}

function makeGetConversationLoadingStatus(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
) {
  return createSelector([getContactDetailsPanel], (panel) => panel?.conversationStatus);
}

/*
 * Component.
 */

export const ContactDetailsConversationListContainerComponent: FC<
  ContactDetailsConversationListContainerComponentProps
> = (props) => {
  const {
    panelId,
    conversations,
    conversationLoadingStatus,
    conversationIdsToHighlight,
    hasMoreConversations,
    panel,
    searchHistory,
    markHighlightAsViewed,
    fetchConversations,
    onConversationClick,
    onConversationHoverTrack,
  } = props;

  const conversationsRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  useEffect(() => {
    // Make sure we are loaded, have a target to scroll too and we want to scroll to conversations.
    if (
      panel?.sectionToScrollTo !== ContactDetailsScrollSectionsEnum.CONVERSATIONS ||
      panel?.conversationStatus !== AsyncStatusesEnum.LOADED ||
      !conversationsRef.current
    ) {
      return;
    }
    conversationsRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    dispatch(contactDetailsScrolledToSectionAction(panel.id));
  }, [panel?.conversationStatus, panel?.id, panel?.sectionToScrollTo, dispatch]);

  return (
    <ConversationList
      panelId={panelId}
      conversationsRef={conversationsRef}
      conversations={conversations}
      conversationLoadingStatus={conversationLoadingStatus}
      conversationIdsToHighlight={conversationIdsToHighlight}
      hasMoreConversations={hasMoreConversations}
      searchHistory={searchHistory}
      markHighlightAsViewed={markHighlightAsViewed}
      fetchConversations={fetchConversations}
      onConversationClick={onConversationClick}
      onConversationHoverTrack={onConversationHoverTrack}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactDetailsConversationListContainerProps>()(() => {
  const getContactDetailsPanel = makeGetContactDetailsPanel();
  return {
    panel: getContactDetailsPanel,
    conversationLoadingStatus: makeGetConversationLoadingStatus(getContactDetailsPanel),
    conversations: makeGetConversations(makeGetConversationIds(getContactDetailsPanel)),
    conversationIdsToHighlight: makeGetConversationIdsToHighlight(),
    hasMoreConversations: makeGetHasMoreConversations(getContactDetailsPanel),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ContactDetailsConversationListContainerProps>()(
  ({panelId, card, visualContact}) => ({
    searchHistory: (): ThunkAction => (dispatch) => {
      const searchParameters = {
        location: InboxesSearchLocationsEnum.GLOBAL,
        query: `history:${visualContact.contact.handle}`,
      };
      dispatch(searchByParametersAction(searchParameters));
      if (card) {
        trackInterfaceEvent(
          InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
          buildClickTrackingPayload(
            ContactDetailsClickActionsEnum.SEARCH_ALL_CONVOS,
            ContactDetailsClickLocationsEnum.BODY,
            card,
          ),
        );
      }
    },
    markHighlightAsViewed: () => markHighlightAsViewedAction(visualContact.contact),
    fetchConversations: () => fetchContactDetailsConversationsAction(panelId, visualContact.contact),
  }),
);

export const ContactDetailsConversationListContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    onConversationClick: (conversation: ConversationModel) => {
      const {conversations} = stateProps;
      const {card} = ownProps;
      if (!card) {
        return;
      }

      const [recentConversations] = splitConversationsIntoRecentAndOlder(conversations);
      const section = computeSectionForAnalytics(
        conversation,
        recentConversations,
        recentConversations.length > 0,
      );
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_OPEN_CONVERSATION,
        buildOpenConversationPayload(card, section),
      );
    },
  }),
)(ContactDetailsConversationListContainerComponent);

/*
 * Helpers.
 */

function computeSectionForAnalytics(
  conversation: ConversationModel,
  recentConversations: ReadonlyArray<ConversationModel>,
  isShowingSubtitle: boolean,
) {
  if (!isShowingSubtitle) {
    return ContactDetailsOpenConversationSectionsEnum.NONE;
  }

  if (recentConversations.includes(conversation)) {
    return ContactDetailsOpenConversationSectionsEnum.LAST_7D;
  }

  return ContactDetailsOpenConversationSectionsEnum.OLDER;
}
